/**
 * Content-aware column sizing for the post-run layout pass.
 *
 * Host auto-fit on its own cannot widen a column whose cells wrap: Excel grows
 * the row height and leaves the width alone, so a generated table that mixes
 * narrative commentary with numeric columns ends up rendering the commentary
 * one word (or one letter) per line. This module derives, from the values a run
 * actually wrote, which columns are narrative and what bounded width they
 * should be given, and leaves every compact column on plain auto-fit.
 *
 * Pure and host-free, so the policy is unit-testable without Office.js or the
 * FactSet intent bridge.
 */

import { EXCEL_MAX_COLUMN, EXCEL_MAX_ROW } from "./excelCellAddress";
import type { WorkbookModification } from "./types";

/** One worksheet-scoped address the sizing pass can act on. */
export interface ColumnSpanTarget {
  sheetName: string;
  address: string;
}

/** A column span plus the explicit width to apply to it. */
export interface ColumnWidthTarget extends ColumnSpanTarget {
  width: number;
  /** Written cells to wrap, only after this width has been applied. */
  wrapTextTargets: ColumnSpanTarget[];
  /** Written rows to re-fit after wrapping succeeds. */
  rowFitTargets: ColumnSpanTarget[];
}

/** Identity of a single worksheet column. */
export interface ColumnSizingKey {
  sheetName: string;
  /** Column letters, e.g. `"D"`. */
  column: string;
}

/**
 * Accumulated text evidence for one written column. Widths are derived from
 * this at plan time rather than stored, so evidence from separate writes to the
 * same column composes (a table populated row by row still qualifies).
 */
export interface ColumnSizingHint extends ColumnSizingKey {
  /** Longest single written line in this column, in characters. */
  longestTextChars: number;
  /** How many written values reached `NARRATIVE_TEXT_MIN_CHARS`. */
  longTextCount: number;
}

/** Final text evidence for one written cell, keyed by normalized sheet + A1 address. */
export interface ColumnSizingCellEvidence extends ColumnSizingKey {
  row: number;
  textChars: number;
}

export type ColumnSizingCellEvidenceMap = Record<string, ColumnSizingCellEvidence>;

export interface ColumnSizingPlan {
  /** Columns that keep plain host auto-fit. */
  autofitColumnTargets: ColumnSpanTarget[];
  /** Narrative columns plus the bounded cell and row work tied to each width. */
  columnWidthTargets: ColumnWidthTarget[];
}

/** Longest written line at which a column stops counting as compact. */
export const NARRATIVE_TEXT_MIN_CHARS = 32;
/** Floor for a narrative width, so a hint can never produce a cramped column. */
export const NARRATIVE_COLUMN_MIN_WIDTH = 20;
/** Ceiling for a narrative width, so long commentary wraps instead of sprawling. */
export const NARRATIVE_COLUMN_MAX_WIDTH = 42;

/** Padding so the widest line is not flush against the cell border. */
const WIDTH_PADDING_CHARS = 2;

/**
 * A lone long value is usually a merged sheet title sitting above the table
 * rather than a narrative column, so a column needs at least this many long
 * values before it earns an explicit width.
 */
const NARRATIVE_VALUE_MIN_COUNT = 2;

/** Matches one A1 area: `A:C`, `B2:C5`, `H7`, with optional `$` markers. */
const AREA_PATTERN =
  /^\$?([A-Z]{1,3})(?:\$?(\d+))?(?:\s*:\s*\$?([A-Z]{1,3})(?:\$?(\d+))?)?$/i;

interface Span {
  start: number;
  end: number;
}

interface ParsedArea {
  columns: Span;
  /** Present only when the area carries row numbers on both ends. */
  rows?: Span;
}

function sheetKeyOf(sheetName: string): string {
  return sheetName.trim().toLowerCase();
}

function columnKeyOf(sheetName: string, column: string): string {
  return `${sheetKeyOf(sheetName)}!${column.trim().toUpperCase()}`;
}

/** One-based column index for column letters, or `null` when out of range. */
export function excelColumnIndex(label: string): number | null {
  let index = 0;
  for (const char of label.toUpperCase()) {
    const code = char.charCodeAt(0);
    if (code < 65 || code > 90) return null;
    index = index * 26 + code - 64;
  }
  return index >= 1 && index <= EXCEL_MAX_COLUMN ? index : null;
}

/** Column letters for a one-based column index. */
export function excelColumnName(index: number): string {
  let value = index;
  let name = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - 1) / 26);
  }
  return name;
}

/**
 * Split a possibly multi-area address on commas, ignoring commas inside a
 * quoted sheet name.
 */
export function splitRangeAreas(address: string): string[] {
  const areas: string[] = [];
  let areaStart = 0;
  let inQuotedSheetName = false;

  for (let index = 0; index < address.length; index += 1) {
    if (address[index] === "'") {
      if (inQuotedSheetName && address[index + 1] === "'") {
        index += 1;
      } else {
        inQuotedSheetName = !inQuotedSheetName;
      }
      continue;
    }
    if (address[index] === "," && !inQuotedSheetName) {
      areas.push(address.slice(areaStart, index));
      areaStart = index + 1;
    }
  }
  areas.push(address.slice(areaStart));
  return areas;
}

function stripSheetQualifier(area: string): string {
  const qualifierIndex = area.lastIndexOf("!");
  return qualifierIndex >= 0 ? area.slice(qualifierIndex + 1).trim() : area;
}

function parseRow(value: string | undefined): number | null {
  if (value === undefined) return null;
  if (value.length > 1 && value[0] === "0") return null;
  const row = Number(value);
  if (!Number.isSafeInteger(row) || row < 1 || row > EXCEL_MAX_ROW) return null;
  return row;
}

/** Parse one worksheet-local A1 area into column and (when present) row spans. */
function parseArea(area: string): ParsedArea | null {
  const match = AREA_PATTERN.exec(area.trim());
  if (!match) return null;
  const startColumn = excelColumnIndex(match[1]);
  const endColumn = excelColumnIndex(match[3] ?? match[1]);
  if (startColumn === null || endColumn === null) return null;

  const columns: Span = {
    start: Math.min(startColumn, endColumn),
    end: Math.max(startColumn, endColumn),
  };
  const startRow = parseRow(match[2]);
  const endRow = match[3] === undefined ? startRow : parseRow(match[4]);
  if (startRow === null || endRow === null) return { columns };
  return {
    columns,
    rows: {
      start: Math.min(startRow, endRow),
      end: Math.max(startRow, endRow),
    },
  };
}

/** Parse a single-area address, rejecting multi-area and row-only shapes. */
function parseSingleArea(address: unknown): ParsedArea | null {
  if (typeof address !== "string") return null;
  const trimmed = address.trim();
  if (!trimmed) return null;
  const areas = splitRangeAreas(trimmed);
  if (areas.length !== 1) return null;
  return parseArea(stripSheetQualifier(areas[0].trim()));
}

/**
 * Merge overlapping and adjacent spans. Adjacent spans are merged so a run of
 * neighbouring single columns collapses into one host call.
 */
function coalesceSpans(spans: readonly Span[]): Span[] {
  const sorted = spans
    .slice()
    .sort((left, right) => left.start - right.start || left.end - right.end);
  const merged: Span[] = [];
  for (const span of sorted) {
    const active = merged[merged.length - 1];
    if (active && span.start <= active.end + 1) {
      active.end = Math.max(active.end, span.end);
      continue;
    }
    merged.push({ ...span });
  }
  return merged;
}

/**
 * Longest single line of user-visible text in a written value. Numbers,
 * booleans, blanks and formulas contribute nothing: their rendered width is not
 * knowable from the payload, and they are what plain auto-fit already handles.
 */
export function longestTextLine(value: unknown): number {
  if (typeof value !== "string") return 0;
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith("=")) return 0;
  let longest = 0;
  for (const line of trimmed.split(/\r?\n/)) {
    longest = Math.max(longest, line.trim().length);
  }
  return longest;
}

/**
 * Bounded width for a narrative column, or `null` when the column is compact
 * enough that plain auto-fit already reads well.
 */
export function narrativeColumnWidth(
  hint: Pick<ColumnSizingHint, "longestTextChars" | "longTextCount">,
): number | null {
  const { longestTextChars, longTextCount } = hint;
  if (!Number.isFinite(longestTextChars) || !Number.isFinite(longTextCount)) {
    return null;
  }
  if (longTextCount < NARRATIVE_VALUE_MIN_COUNT) return null;
  if (longestTextChars < NARRATIVE_TEXT_MIN_CHARS) return null;
  return Math.min(
    NARRATIVE_COLUMN_MAX_WIDTH,
    Math.max(
      NARRATIVE_COLUMN_MIN_WIDTH,
      Math.round(longestTextChars) + WIDTH_PADDING_CHARS,
    ),
  );
}

/**
 * Collect per-column text evidence from the writes a host apply confirmed.
 * Mirrors the write shapes that feed the final auto-fit pass: `set` cells and
 * `fill` ranges.
 */
export function collectColumnSizingHints(
  modifications: WorkbookModification,
): ColumnSizingHint[] {
  const byKey = new Map<string, ColumnSizingHint>();

  const record = (sheetName: string, column: number, chars: number): void => {
    if (chars <= 0 || column < 1 || column > EXCEL_MAX_COLUMN) return;
    const columnLabel = excelColumnName(column);
    const key = columnKeyOf(sheetName, columnLabel);
    const existing = byKey.get(key);
    const isLong = chars >= NARRATIVE_TEXT_MIN_CHARS;
    if (!existing) {
      byKey.set(key, {
        sheetName,
        column: columnLabel,
        longestTextChars: chars,
        longTextCount: isLong ? 1 : 0,
      });
      return;
    }
    existing.longestTextChars = Math.max(existing.longestTextChars, chars);
    if (isLong) existing.longTextCount += 1;
  };

  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName =
      typeof worksheet.sheetName === "string" ? worksheet.sheetName.trim() : "";
    if (!sheetName) continue;

    for (const cell of worksheet.cells ?? []) {
      if (cell.action !== "set") continue;
      const area = parseSingleArea(cell.address);
      if (!area) continue;
      const chars = longestTextLine(cell.value);
      if (chars <= 0) continue;
      for (let column = area.columns.start; column <= area.columns.end; column += 1) {
        record(sheetName, column, chars);
      }
    }

    for (const range of worksheet.ranges ?? []) {
      if (range.action !== "fill") continue;
      const area = parseSingleArea(range.address);
      if (!area || !Array.isArray(range.values)) continue;
      for (const row of range.values) {
        if (!Array.isArray(row)) continue;
        for (let offset = 0; offset < row.length; offset += 1) {
          const column = area.columns.start + offset;
          if (column > area.columns.end) break;
          record(sheetName, column, longestTextLine(row[offset]));
        }
      }
    }
  }

  return Array.from(byKey.values());
}

function cellEvidenceKey(sheetName: string, column: string, row: number): string {
  return `${columnKeyOf(sheetName, column)}${row}`;
}

function clearCellEvidenceArea(
  evidence: ColumnSizingCellEvidenceMap,
  sheetName: string,
  area: ParsedArea,
): void {
  const sheetKey = sheetKeyOf(sheetName);
  for (const [key, entry] of Object.entries(evidence)) {
    if (sheetKeyOf(entry.sheetName) !== sheetKey) continue;
    const column = excelColumnIndex(entry.column);
    if (
      column === null ||
      column < area.columns.start ||
      column > area.columns.end
    ) {
      continue;
    }
    if (
      area.rows &&
      (entry.row < area.rows.start || entry.row > area.rows.end)
    ) {
      continue;
    }
    delete evidence[key];
  }
}

function setCellEvidence(
  evidence: ColumnSizingCellEvidenceMap,
  sheetName: string,
  column: number,
  row: number,
  value: unknown,
): void {
  const columnLabel = excelColumnName(column);
  const key = cellEvidenceKey(sheetName, columnLabel, row);
  const textChars = longestTextLine(value);
  if (textChars <= 0) {
    delete evidence[key];
    return;
  }
  evidence[key] = {
    sheetName,
    column: columnLabel,
    row,
    textChars,
  };
}

function updateColumnSizingCellEvidence(
  evidence: ColumnSizingCellEvidenceMap,
  modifications: WorkbookModification,
): void {
  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName =
      typeof worksheet.sheetName === "string" ? worksheet.sheetName.trim() : "";
    if (!sheetName) continue;

    for (const cell of worksheet.cells ?? []) {
      const area = parseSingleArea(cell.address);
      if (!area) continue;
      if (cell.action === "clear") {
        clearCellEvidenceArea(evidence, sheetName, area);
        continue;
      }
      if (
        cell.action !== "set" ||
        !area.rows ||
        (cell.value === undefined && cell.formula === undefined)
      ) {
        continue;
      }
      const value =
        typeof cell.formula === "string" && cell.formula.trim()
          ? cell.formula
          : cell.value;
      for (let row = area.rows.start; row <= area.rows.end; row += 1) {
        for (
          let column = area.columns.start;
          column <= area.columns.end;
          column += 1
        ) {
          setCellEvidence(evidence, sheetName, column, row, value);
        }
      }
    }

    for (const range of worksheet.ranges ?? []) {
      const area = parseSingleArea(range.address);
      if (!area) continue;
      if (
        range.action === "clear" &&
        ["All", "Contents"].includes(range.clear?.clearType ?? "All")
      ) {
        clearCellEvidenceArea(evidence, sheetName, area);
        continue;
      }
      if (range.action !== "fill" || !area.rows) continue;
      const values = range.values ?? [];
      const formulas = range.formulas ?? [];
      const rowCount = Math.min(
        Math.max(values.length, formulas.length),
        area.rows.end - area.rows.start + 1,
      );
      for (let rowOffset = 0; rowOffset < rowCount; rowOffset += 1) {
        const valueRow = values[rowOffset] ?? [];
        const formulaRow = formulas[rowOffset] ?? [];
        const columnCount = Math.min(
          Math.max(valueRow.length, formulaRow.length),
          area.columns.end - area.columns.start + 1,
        );
        for (
          let columnOffset = 0;
          columnOffset < columnCount;
          columnOffset += 1
        ) {
          const hasFormula = columnOffset < formulaRow.length;
          const hasValue = columnOffset < valueRow.length;
          if (!hasFormula && !hasValue) continue;
          const formula = formulaRow[columnOffset];
          const value =
            typeof formula === "string" && formula.trim()
              ? formula
              : hasValue
                ? valueRow[columnOffset]
                : formula;
          setCellEvidence(
            evidence,
            sheetName,
            area.columns.start + columnOffset,
            area.rows.start + rowOffset,
            value,
          );
        }
      }
    }
  }
}

function summarizeColumnSizingCellEvidence(
  evidence: ColumnSizingCellEvidenceMap,
): ColumnSizingHint[] {
  const byKey = new Map<string, ColumnSizingHint>();
  for (const entry of Object.values(evidence)) {
    if (!Number.isFinite(entry.textChars) || entry.textChars <= 0) continue;
    const key = columnKeyOf(entry.sheetName, entry.column);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        sheetName: entry.sheetName,
        column: entry.column,
        longestTextChars: entry.textChars,
        longTextCount:
          entry.textChars >= NARRATIVE_TEXT_MIN_CHARS ? 1 : 0,
      });
      continue;
    }
    existing.longestTextChars = Math.max(
      existing.longestTextChars,
      entry.textChars,
    );
    if (entry.textChars >= NARRATIVE_TEXT_MIN_CHARS) {
      existing.longTextCount += 1;
    }
  }
  return Array.from(byKey.values());
}

/**
 * Columns the run sized explicitly. These are excluded from automatic sizing so
 * the final pass cannot overwrite a width the user or model asked for.
 */
export function collectExplicitColumnWidthKeys(
  modifications: WorkbookModification,
): ColumnSizingKey[] {
  const byKey = new Map<string, ColumnSizingKey>();

  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName =
      typeof worksheet.sheetName === "string" ? worksheet.sheetName.trim() : "";
    if (!sheetName) continue;
    for (const range of worksheet.ranges ?? []) {
      if (range.action !== "resize") continue;
      if (typeof range.width !== "number" || !Number.isFinite(range.width)) {
        continue;
      }
      const area = parseSingleArea(range.address);
      if (!area) continue;
      for (let column = area.columns.start; column <= area.columns.end; column += 1) {
        const columnLabel = excelColumnName(column);
        byKey.set(columnKeyOf(sheetName, columnLabel), {
          sheetName,
          column: columnLabel,
        });
      }
    }
  }

  return Array.from(byKey.values());
}

/** Merge accumulated evidence with a new batch, keeping the strongest signal. */
export function mergeColumnSizingHints(
  existing: readonly ColumnSizingHint[],
  incoming: readonly ColumnSizingHint[],
): ColumnSizingHint[] {
  const byKey = new Map<string, ColumnSizingHint>();
  for (const hint of [...existing, ...incoming]) {
    const sheetName = hint.sheetName.trim();
    const column = hint.column.trim().toUpperCase();
    if (!sheetName || !column) continue;
    const key = columnKeyOf(sheetName, column);
    const current = byKey.get(key);
    if (!current) {
      byKey.set(key, {
        sheetName,
        column,
        longestTextChars: hint.longestTextChars,
        longTextCount: hint.longTextCount,
      });
      continue;
    }
    current.longestTextChars = Math.max(
      current.longestTextChars,
      hint.longestTextChars,
    );
    current.longTextCount += hint.longTextCount;
  }
  return Array.from(byKey.values());
}

/** Merge two column key lists, preserving first-seen order. */
export function mergeColumnSizingKeys(
  existing: readonly ColumnSizingKey[],
  incoming: readonly ColumnSizingKey[],
): ColumnSizingKey[] {
  const byKey = new Map<string, ColumnSizingKey>();
  for (const entry of [...existing, ...incoming]) {
    const sheetName = entry.sheetName.trim();
    const column = entry.column.trim().toUpperCase();
    if (!sheetName || !column) continue;
    const key = columnKeyOf(sheetName, column);
    if (!byKey.has(key)) byKey.set(key, { sheetName, column });
  }
  return Array.from(byKey.values());
}

/** Mutable slice of the run accumulators this policy owns. */
export interface ColumnSizingAccumulatorSlice {
  columnSizingHints?: ColumnSizingHint[];
  columnSizingCellEvidence?: ColumnSizingCellEvidenceMap;
  explicitColumnWidthKeys?: ColumnSizingKey[];
}

/**
 * Fold one confirmed modification payload into the run's sizing evidence.
 * Called alongside the auto-fit target bookkeeping so both stay in step.
 */
export function recordColumnSizingIntent(
  accumulators: ColumnSizingAccumulatorSlice,
  modifications: WorkbookModification,
): void {
  const evidence = accumulators.columnSizingCellEvidence ?? {};
  updateColumnSizingCellEvidence(evidence, modifications);
  accumulators.columnSizingCellEvidence = evidence;
  accumulators.columnSizingHints =
    summarizeColumnSizingCellEvidence(evidence);
  const explicitKeys = collectExplicitColumnWidthKeys(modifications);
  if (explicitKeys.length > 0) {
    accumulators.explicitColumnWidthKeys = mergeColumnSizingKeys(
      accumulators.explicitColumnWidthKeys ?? [],
      explicitKeys,
    );
  }
}

/**
 * Reduce write targets to whole-column spans per sheet. Areas that cannot be
 * parsed are passed through verbatim so the host still gets a chance at them.
 */
export function coalesceColumnSpanTargets(
  targets: Iterable<ColumnSpanTarget>,
): ColumnSpanTarget[] {
  const bySheet = new Map<
    string,
    {
      sheetName: string;
      spans: Span[];
      fallbacks: Map<string, string>;
    }
  >();

  for (const target of Array.from(targets)) {
    const sheetName = target.sheetName.trim();
    const address = target.address.trim();
    if (!sheetName || !address) continue;
    const sheetKey = sheetKeyOf(sheetName);
    let sheet = bySheet.get(sheetKey);
    if (!sheet) {
      sheet = { sheetName, spans: [], fallbacks: new Map() };
      bySheet.set(sheetKey, sheet);
    }

    for (const rawArea of splitRangeAreas(address)) {
      const trimmedArea = rawArea.trim();
      if (!trimmedArea) continue;
      const area = parseArea(stripSheetQualifier(trimmedArea));
      if (area) {
        sheet.spans.push({ ...area.columns });
      } else {
        sheet.fallbacks.set(trimmedArea.toUpperCase(), trimmedArea);
      }
    }
  }

  const coalesced: ColumnSpanTarget[] = [];
  for (const sheet of Array.from(bySheet.values())) {
    for (const span of coalesceSpans(sheet.spans)) {
      coalesced.push({
        sheetName: sheet.sheetName,
        address: `${excelColumnName(span.start)}:${excelColumnName(span.end)}`,
      });
    }
    for (const address of Array.from(sheet.fallbacks.values())) {
      coalesced.push({ sheetName: sheet.sheetName, address });
    }
  }
  return coalesced;
}

/**
 * Split the run's write targets into the three sizing passes: auto-fit for
 * compact columns, bounded explicit widths for narrative columns, and a row
 * re-fit for the rows those narrative columns occupy.
 */
export function planColumnSizing(opts: {
  targets: Iterable<ColumnSpanTarget>;
  hints?: readonly ColumnSizingHint[];
  explicitWidthColumns?: readonly ColumnSizingKey[];
}): ColumnSizingPlan {
  const targets = Array.from(opts.targets);
  const explicitKeys = new Set(
    (opts.explicitWidthColumns ?? []).map((entry) =>
      columnKeyOf(entry.sheetName, entry.column),
    ),
  );

  const widthByKey = new Map<string, number>();
  const widthsBySheet = new Map<
    string,
    { sheetName: string; columns: Map<number, number> }
  >();
  for (const hint of mergeColumnSizingHints([], opts.hints ?? [])) {
    const key = columnKeyOf(hint.sheetName, hint.column);
    if (explicitKeys.has(key)) continue;
    const width = narrativeColumnWidth(hint);
    const column = excelColumnIndex(hint.column);
    if (width === null || column === null) continue;
    widthByKey.set(key, width);
    const sheetKey = sheetKeyOf(hint.sheetName);
    let sheet = widthsBySheet.get(sheetKey);
    if (!sheet) {
      sheet = { sheetName: hint.sheetName.trim(), columns: new Map() };
      widthsBySheet.set(sheetKey, sheet);
    }
    sheet.columns.set(column, width);
  }

  const columnWidthTargets: ColumnWidthTarget[] = [];
  for (const sheet of Array.from(widthsBySheet.values())) {
    const columns = Array.from(sheet.columns.keys()).sort(
      (left, right) => left - right,
    );
    let index = 0;
    while (index < columns.length) {
      const start = columns[index];
      const width = sheet.columns.get(start) as number;
      let end = start;
      while (
        index + 1 < columns.length &&
        columns[index + 1] === end + 1 &&
        sheet.columns.get(columns[index + 1]) === width
      ) {
        index += 1;
        end = columns[index];
      }
      columnWidthTargets.push({
        sheetName: sheet.sheetName,
        address: `${excelColumnName(start)}:${excelColumnName(end)}`,
        width,
        wrapTextTargets: [],
        rowFitTargets: [],
      });
      index += 1;
    }
  }

  const excludedBySheet = new Map<string, Set<number>>();
  const addExcluded = (sheetName: string, column: string): void => {
    const index = excelColumnIndex(column);
    if (index === null) return;
    const sheetKey = sheetKeyOf(sheetName);
    let columns = excludedBySheet.get(sheetKey);
    if (!columns) {
      columns = new Set();
      excludedBySheet.set(sheetKey, columns);
    }
    columns.add(index);
  };
  for (const entry of opts.explicitWidthColumns ?? []) {
    addExcluded(entry.sheetName, entry.column);
  }
  for (const key of Array.from(widthByKey.keys())) {
    const separator = key.lastIndexOf("!");
    addExcluded(key.slice(0, separator), key.slice(separator + 1));
  }

  const autofitColumnTargets: ColumnSpanTarget[] = [];
  for (const target of coalesceColumnSpanTargets(targets)) {
    const excluded = excludedBySheet.get(sheetKeyOf(target.sheetName));
    const area = excluded ? parseArea(target.address) : null;
    if (!area) {
      autofitColumnTargets.push(target);
      continue;
    }
    const kept: Span[] = [];
    for (let column = area.columns.start; column <= area.columns.end; column += 1) {
      if (excluded?.has(column)) continue;
      kept.push({ start: column, end: column });
    }
    for (const span of coalesceSpans(kept)) {
      autofitColumnTargets.push({
        sheetName: target.sheetName,
        address: `${excelColumnName(span.start)}:${excelColumnName(span.end)}`,
      });
    }
  }

  for (const widthTarget of columnWidthTargets) {
    const widthArea = parseArea(widthTarget.address);
    if (!widthArea) continue;
    const wrapRowsByColumns = new Map<
      string,
      { columns: Span; rows: Span[] }
    >();
    const rowSpans: Span[] = [];

    for (const target of targets) {
      const sheetName = target.sheetName.trim();
      if (sheetKeyOf(sheetName) !== sheetKeyOf(widthTarget.sheetName)) continue;
      for (const rawArea of splitRangeAreas(target.address.trim())) {
        const area = parseArea(stripSheetQualifier(rawArea.trim()));
        if (!area?.rows) continue;
        const columns = {
          start: Math.max(area.columns.start, widthArea.columns.start),
          end: Math.min(area.columns.end, widthArea.columns.end),
        };
        if (columns.start > columns.end) continue;
        const key = `${columns.start}:${columns.end}`;
        let wrapGroup = wrapRowsByColumns.get(key);
        if (!wrapGroup) {
          wrapGroup = { columns, rows: [] };
          wrapRowsByColumns.set(key, wrapGroup);
        }
        wrapGroup.rows.push({ ...area.rows });
        rowSpans.push({ ...area.rows });
      }
    }

    for (const group of Array.from(wrapRowsByColumns.values())) {
      for (const rows of coalesceSpans(group.rows)) {
        widthTarget.wrapTextTargets.push({
          sheetName: widthTarget.sheetName,
          address:
            `${excelColumnName(group.columns.start)}${rows.start}:` +
            `${excelColumnName(group.columns.end)}${rows.end}`,
        });
      }
    }
    for (const rows of coalesceSpans(rowSpans)) {
      widthTarget.rowFitTargets.push({
        sheetName: widthTarget.sheetName,
        address: `${rows.start}:${rows.end}`,
      });
    }
  }

  return { autofitColumnTargets, columnWidthTargets };
}
